import { apiFetch } from "../../../services/apiFetch";

/*
 * Insights V1 read endpoints (backend/routes/insights.js, behind
 * requireSession). Identity is the authenticated session only: these calls
 * never carry a user or a cycle, and the backend rejects unknown query keys.
 *
 * The clock is the device timezone, the same convention every other
 * date-sensitive read of the app uses (services/api.js `getHomeDashboard`,
 * TRAIN hub): it is the calendar the athlete is living in.
 */

export const CURRENT_ANALYSIS = "CURRENT_ANALYSIS";
export const HISTORICAL_TIMEFRAMES = Object.freeze([
  "3M",
  "6M",
  "1Y",
  "2Y",
  "5Y",
  "10Y",
  "ALL_TIME",
]);
export const INSIGHTS_TIMEFRAMES = Object.freeze([
  CURRENT_ANALYSIS,
  ...HISTORICAL_TIMEFRAMES,
]);

export function isInsightsTimeframe(value) {
  return INSIGHTS_TIMEFRAMES.includes(value);
}

export function getDeviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";
  } catch {
    return "America/Toronto";
  }
}

function insightsPath(resource, query) {
  return `/api/insights/${resource}?${new URLSearchParams(query).toString()}`;
}

// `timezone` defaults to the device's; a caller that also caches the response
// passes the same value it keys the cache with.
export function getCurrentAnalysis(timezone = getDeviceTimezone()) {
  return apiFetch(insightsPath("current-analysis", { timezone }));
}

function assertTimeframe(timeframe) {
  if (!isInsightsTimeframe(timeframe)) {
    throw new Error(`Unknown Insights timeframe: ${String(timeframe)}`);
  }
}

export function getInsightsAdherence(timeframe, timezone = getDeviceTimezone()) {
  assertTimeframe(timeframe);
  return apiFetch(insightsPath("adherence", { timeframe, timezone }));
}

export function getInsightsMuscleVolume(timeframe, timezone = getDeviceTimezone()) {
  assertTimeframe(timeframe);
  return apiFetch(insightsPath("muscle-volume", { timeframe, timezone }));
}

// Exercise Performance (EP V1, blueprint §5.2–§5.4). The exercise id is a path
// segment, always encoded; identity is still the session only.
function exercisePath(exerciseId, suffix = "") {
  if (typeof exerciseId !== "string" || !exerciseId) {
    throw new Error(`Unknown exercise id: ${String(exerciseId)}`);
  }
  return `exercise-performance/exercises/${encodeURIComponent(exerciseId)}${suffix}`;
}

export function getExercisePerformance(timeframe, timezone = getDeviceTimezone()) {
  assertTimeframe(timeframe);
  return apiFetch(insightsPath("exercise-performance", { timeframe, timezone }));
}

export function getExerciseDetail(exerciseId, timeframe, timezone = getDeviceTimezone()) {
  assertTimeframe(timeframe);
  return apiFetch(insightsPath(exercisePath(exerciseId), { timeframe, timezone }));
}

// One "Load more" page; `cursor` is the previous page's opaque nextCursor.
export function getExerciseSessions(exerciseId, timeframe, timezone = getDeviceTimezone(), cursor = null) {
  assertTimeframe(timeframe);
  const query = { timeframe, timezone };
  if (cursor) query.cursor = cursor;
  return apiFetch(insightsPath(exercisePath(exerciseId, "/sessions"), query));
}
